// Country Portal — Dynamic branding by country code.
//
// Single build, six deployments. Each country server sets one of:
//   REACT_APP_PORTAL_COUNTRY=IN | UK | MY | SG | JP | PH     (2-letter, preferred)
//   REACT_APP_COUNTRY=india | uk | malaysia | singapore | japan | philippines  (legacy, still supported)
//
// Since CRA bakes REACT_APP_* at build time, each server must have the correct
// value in its .env file before running `npm run build`.
//
// Fallback order: REACT_APP_PORTAL_COUNTRY -> REACT_APP_COUNTRY -> 'IN'.

export type PortalCode = 'IN' | 'UK' | 'MY' | 'SG' | 'JP' | 'PH';

export interface PortalBrand {
  code: PortalCode;
  name: string;          // "IndiaPolls", "UKPolls", etc. — user-visible brand name
  shortName: string;     // 2-letter code for compact UI
  domain: string;        // public domain (no protocol)
  logo: string;          // path under /public — primary logo
  logoFront: String;  
  favicon: string;       // path under /public — favicon
  loginBg: string;       // path under /public — login background image (may be empty to use color only)
  primaryColor: string;  // hex — used for theme accents
  secondaryColor: string; // hex — second flag color (empty string '' if N/A)
  accentColor: string;   // hex — third flag color (empty string '' if N/A)
  flag: string;          // flag emoji
  tagline: string;       // short marketing tagline
  pageTitle: string;     // document.title
}

const BRANDS: Record<PortalCode, PortalBrand> = {
  IN: {
    code: 'IN',
    name: 'IndiaPolls',
    shortName: 'IN',
    domain: 'indiapolls.com',
    logo: '/branding/indiapolls-logo.png',
    logoFront: '/branding/indiapolls-logo-front.png',
    favicon: '/branding/indiapolls-favicon.png',
    loginBg: '/branding/indiapolls-bg-pattern.png',
    primaryColor: '#FF9933',
    secondaryColor: '#FFFFFF',
    accentColor: '#138808',
    flag: '\u{1F1EE}\u{1F1F3}',
    tagline: 'Your Voice Matters',
    pageTitle: 'IndiaPolls Panel',
  },
  UK: {
    code: 'UK',
    name: 'UKPolls',
    shortName: 'UK',
    domain: 'ukpolls.com',
    logo: '/branding/ukpolls-logo.png',
    logoFront: '/branding/ukpolls-logo-front.png',
    favicon: '/branding/ukpolls-favicon.png',
    loginBg: '/branding/ukpolls-bg-pattern.png',
    primaryColor: '#012169',
    secondaryColor: '#FFFFFF',
    accentColor: '#C8102E',
    flag: '\u{1F1EC}\u{1F1E7}',
    tagline: 'Your Voice Matters',
    pageTitle: 'UKPolls Panel',
  },
  MY: {
    code: 'MY',
    name: 'MalaysiaPolls',
    shortName: 'MY',
    domain: 'malaysiapolls.com',
    logo: '/branding/malaysiapolls-logo.png',
    logoFront: '/branding/malaysiapolls-logo-front.png',
    favicon: '/branding/malaysiapolls-favicon.png',
    loginBg: '/branding/malaysiapolls-bg-pattern.png',
    primaryColor: '#010066',
    secondaryColor: '#CC0001',
    accentColor: '#FFCC00',
    flag: '\u{1F1F2}\u{1F1FE}',
    tagline: 'Your Voice Matters',
    pageTitle: 'MalaysiaPolls Panel',
  },
  SG: {
    code: 'SG',
    name: 'SingaporePolls',
    shortName: 'SG',
    domain: 'singapore-polls.com',
    logo: '/branding/singaporepolls-logo.png',
    logoFront: '/branding/singapore-cd polls-logo-front.png',
    favicon: '/branding/singaporepolls-favicon.png',
    loginBg: '/branding/singaporepolls-bg-pattern.png',
    primaryColor: '#EF3340',
    secondaryColor: '#FFFFFF',
    accentColor: '',
    flag: '\u{1F1F8}\u{1F1EC}',
    tagline: 'Your Voice Matters',
    pageTitle: 'SingaporePolls Panel',
  },
  JP: {
    code: 'JP',
    name: 'NipponPolls',
    shortName: 'JP',
    domain: 'nipponpolls.com',
    logo: '/branding/nipponpolls-logo.png',
    logoFront: '/branding/nipponpolls-logo-front.png',
    favicon: '/branding/nipponpolls-favicon.png',
    // Red triangle/tessellation pattern (downscaled 3375->1200px, ~255KB).
    // Rendered as the auth left-panel background under a #D10F17 scrim
    // (see Login.css [data-country="JP"]). Other portals leave loginBg as a
    // solid-color panel — the pattern is JP-only.
    loginBg: '/branding/nipponpolls-bg-pattern.png',
    // Darkest dominant triangle tone from the NipponPolls pattern. Single
    // token: drives auth (--dx-primary), the LanguageToggle active pill, and
    // the material-dashboard portal/admin theme via shades(branding.primaryColor).
    primaryColor: '#D10F17',
    secondaryColor: '#FFFFFF',
    accentColor: '',
    flag: '\u{1F1EF}\u{1F1F5}',
    tagline: 'Your Voice Matters',
    pageTitle: 'NipponPolls Panel',
  },
  PH: {
    code: 'PH',
    name: 'PhilippinesPolls',
    shortName: 'PH',
    domain: 'philippinespolls.com',
    logo: '/branding/philippinespolls-logo.png',
    logoFront: '/branding/philippinespolls-logo-front.png',
    favicon: '/branding/philippinespolls-favicon.png',
    loginBg: '/branding/philippines-bg-pattern.png',
    primaryColor: '#0038A8',
    secondaryColor: '#CE1126',
    accentColor: '#FCD116',
    flag: '\u{1F1F5}\u{1F1ED}',
    tagline: 'Your Voice Matters',
    pageTitle: 'PhilippinesPolls Panel',
  },
};

// Accept either 2-letter or legacy full-name values.
const LEGACY_NAME_TO_CODE: Record<string, PortalCode> = {
  india: 'IN',
  uk: 'UK',
  malaysia: 'MY',
  singapore: 'SG',
  japan: 'JP',
  nippon: 'JP',
  philippines: 'PH',
};

function resolvePortalCode(): PortalCode {
  const raw2 = (process.env.REACT_APP_PORTAL_COUNTRY || '').trim().toUpperCase();
  if (raw2 && (BRANDS as Record<string, PortalBrand>)[raw2]) {
    return raw2 as PortalCode;
  }
  const rawLegacy = (process.env.REACT_APP_COUNTRY || '').trim().toLowerCase();
  if (rawLegacy && LEGACY_NAME_TO_CODE[rawLegacy]) {
    return LEGACY_NAME_TO_CODE[rawLegacy];
  }
  return 'IN';
}

export const PORTAL_CODE: PortalCode = resolvePortalCode();
export const branding: PortalBrand = BRANDS[PORTAL_CODE];
export { BRANDS };
export default BRANDS;
